/**
 * Standalone (Preact + htm) visualization — the NEW parallel visualization track.
 *
 * Two concerns:
 *  1. Share links — a manager mints a public, revocable link that renders ONE submission as a self-contained
 *     standalone HTML page (served from the API, self-fetching its data from a token-scoped public endpoint).
 *  2. Per-form AI design — generate / refine a reusable mini-project for a form (one design, every submission of
 *     that form renders through it). A design is `view.js` plus zero or more custom `components/{key}.js` files.
 *     The two streaming endpoints use raw fetch + a ReadableStream reader because axios does not surface
 *     streaming bodies in the browser. SSE envelope matches the backend:
 *       data:{"type":"building|complete|error","data":{...}}\n\n
 *     `complete` carries { files:[{path,content}], summary, savedAt } — structured file-ops are not token-streamed.
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { STANDALONE_VIZ_ENDPOINTS } from '../endpoints';
import { uploadFileToBlob } from './storageService';

/**
 * Normalize a visualization's `type` to the internal lowercase form. The API returns
 * "Visualization"/"Card"/"ListView" (PascalCase, via the global enum converter); legacy rows created before
 * the Type discriminator have no `type` at all. Treat anything that isn't a card or a list as a full
 * visualization — single source of truth so every consumer agrees on the legacy fallback.
 * @param {{ type?: string }} v
 * @returns {'visualization' | 'card' | 'list'}
 */
export const vizType = (v) => {
  const t = String(v?.type).toLowerCase();
  if (t === 'card') return 'card';
  if (t === 'listview' || t === 'list') return 'list';
  return 'visualization';
};

/** Maps the internal lowercase type to the API's PascalCase StandaloneVizType enum member name. */
const VIZ_TYPE_TO_ENUM = { card: 'Card', list: 'ListView', visualization: 'Visualization' };

const standaloneVizService = {
  // ----- Share links -----

  /** @returns {Promise<Array<{id, token, url, createdAt, expiresAt, revokedAt, isActive}>>} */
  listShares: (submissionId) =>
    apiRequest(() => axiosInstance.get(STANDALONE_VIZ_ENDPOINTS.LIST_SHARES(submissionId))),

  /** @param {number} submissionId @param {number|null} [expiresInDays] */
  createShare: (submissionId, expiresInDays = null) =>
    apiRequest(() =>
      axiosInstance.post(STANDALONE_VIZ_ENDPOINTS.CREATE_SHARE(submissionId), {
        expiresInDays: expiresInDays ?? null,
      })
    ),

  revokeShare: (shareId) =>
    apiRequest(() => axiosInstance.delete(STANDALONE_VIZ_ENDPOINTS.REVOKE_SHARE(shareId))),

  // ----- ListView share links (aggregate-over-many-submissions; public /v/list/{token}) -----

  /** @returns {Promise<Array<{id, token, url, createdAt, expiresAt, revokedAt, isActive}>>} */
  listListShares: (formSchemaId, vizId) =>
    apiRequest(() => axiosInstance.get(STANDALONE_VIZ_ENDPOINTS.LIST_VIZ_SHARES(formSchemaId, vizId))),

  /**
   * Mint a public share link for a ListView viz, capturing the effective list config (filter/limit) so the link
   * reproduces what the author was viewing.
   * @param {number} formSchemaId @param {number} vizId
   * @param {object|null} [config] effective list config to snapshot (null → the viz's saved config)
   * @param {number|null} [expiresInDays]
   */
  createListShare: (formSchemaId, vizId, config = null, expiresInDays = null) =>
    apiRequest(() =>
      axiosInstance.post(STANDALONE_VIZ_ENDPOINTS.LIST_VIZ_SHARES(formSchemaId, vizId), {
        config: config ?? null,
        expiresInDays: expiresInDays ?? null,
      })
    ),

  revokeListShare: (shareId) =>
    apiRequest(() => axiosInstance.delete(STANDALONE_VIZ_ENDPOINTS.REVOKE_LIST_VIZ_SHARE(shareId))),

  // ----- Authenticated render + suggestions (shared by every in-app preview) -----

  /**
   * Fetch the self-contained HTML doc for ONE submission (payload inlined). vizId optional →
   * the server renders the form's default visualization. Returns the raw HTML string.
   * @param {number} submissionId
   * @param {number} [vizId]
   * @returns {Promise<string>} the HTML document
   */
  getRenderDoc: (submissionId, vizId) =>
    apiRequest(() =>
      axiosInstance.get(STANDALONE_VIZ_ENDPOINTS.RENDER_DOC(submissionId, vizId), { responseType: 'text' })
    ),

  /**
   * AI-authored prompt suggestions for first-time generation. Optionally grounded in a real submission
   * sample (the server redacts submitter PII and auto-picks the most-recent submission when none is given);
   * `type` ('visualization' | 'card' | 'list') tailors the ideas to a full report, a compact card, or an
   * aggregate list dashboard. Degrades to [] on error so the editor can fall back to its static prompt ideas.
   * @param {number} formSchemaId
   * @param {number} [sampleSubmissionId]
   * @param {'visualization'|'card'|'list'} [type]
   * @returns {Promise<Array<{ title: string, prompt: string }>>}
   */
  getSuggestions: async (formSchemaId, sampleSubmissionId, type) => {
    try {
      const list = await apiRequest(() =>
        axiosInstance.get(STANDALONE_VIZ_ENDPOINTS.SUGGESTIONS(formSchemaId, sampleSubmissionId, type))
      );
      return Array.isArray(list) ? list.filter((s) => s && s.prompt) : [];
    } catch {
      return [];
    }
  },

  // ----- Per-form AI design (the reusable mini-project: view.js + components/*.js) -----

  /** @returns {Promise<{ formSchemaId, hasDesign, files:Array<{path,content}>, viewJs, lastModel, updatedAt, promptHistory }>} */
  getDesign: (formSchemaId) =>
    apiRequest(() => axiosInstance.get(STANDALONE_VIZ_ENDPOINTS.GET_DESIGN(formSchemaId))),

  /** Hand-edit save of the view.js (back-compat single-file alias; server lints; throws on rejection). */
  saveDesign: (formSchemaId, viewJs) =>
    apiRequest(() => axiosInstance.put(STANDALONE_VIZ_ENDPOINTS.SAVE_DESIGN(formSchemaId), { viewJs })),

  /** Hand-edit save of ONE mini-project file (view.js or components/{key}.js); server lints. */
  saveDesignFile: (formSchemaId, path, content) =>
    apiRequest(() => axiosInstance.put(STANDALONE_VIZ_ENDPOINTS.SAVE_DESIGN_FILE(formSchemaId), { path, content })),

  /** Delete the design → kit reverts to its generic auto-view. */
  deleteDesign: (formSchemaId) =>
    apiRequest(() => axiosInstance.delete(STANDALONE_VIZ_ENDPOINTS.DELETE_DESIGN(formSchemaId))),

  /**
   * Stream AI generation of a brand-new mini-project.
   * @param {number} formSchemaId
   * @param {{ prompt: string, sampleSubmissionId?: number|null }} requestData
   * @param {{ onBuilding?: (msg)=>void, onComplete?: (data)=>void, onError?: (msg)=>void }} callbacks
   *        onComplete receives { files:[{path,content}], summary, savedAt }
   * @param {AbortSignal} [signal]
   */
  generateDesignStream: (formSchemaId, requestData, callbacks, signal) =>
    consumeSseStream(STANDALONE_VIZ_ENDPOINTS.GENERATE_DESIGN_STREAM(formSchemaId), requestData, callbacks, signal),

  /**
   * Stream AI refinement of the existing mini-project (same envelope as generate).
   * @param {number} formSchemaId
   * @param {{ message: string, sampleSubmissionId?: number|null }} requestData
   * @param {object} callbacks - same shape as generateDesignStream
   * @param {AbortSignal} [signal]
   */
  refineDesignStream: (formSchemaId, requestData, callbacks, signal) =>
    consumeSseStream(STANDALONE_VIZ_ENDPOINTS.REFINE_DESIGN_STREAM(formSchemaId), requestData, callbacks, signal),

  // ----- Multi-visualization dashboard (NEW: several named visualizations per form, each with its own design) -----

  /** @returns {Promise<Array<{ id, name, description, type:'Visualization'|'Card', isDefault, hasDesign, showInSubmission, updatedAt, lastModel }>>} */
  listVisualizations: (formSchemaId) =>
    apiRequest(() => axiosInstance.get(STANDALONE_VIZ_ENDPOINTS.LIST_VIZ(formSchemaId))),

  /**
   * @param {number} formSchemaId
   * @param {string} name
   * @param {'visualization'|'card'|'list'} [type='visualization'] sent to the API as the PascalCase enum name
   * @returns {Promise<object>} the created visualization
   */
  createVisualization: (formSchemaId, name, type = 'visualization') =>
    apiRequest(() => axiosInstance.post(STANDALONE_VIZ_ENDPOINTS.CREATE_VIZ(formSchemaId), {
      name,
      type: VIZ_TYPE_TO_ENUM[type] || 'Visualization',
    })),

  /** @param {{ name?: string, description?: string }} patch */
  updateVisualization: (formSchemaId, vizId, { name, description } = {}) =>
    apiRequest(() => axiosInstance.put(STANDALONE_VIZ_ENDPOINTS.UPDATE_VIZ(formSchemaId, vizId), { name, description })),

  deleteVisualization: (formSchemaId, vizId) =>
    apiRequest(() => axiosInstance.delete(STANDALONE_VIZ_ENDPOINTS.DELETE_VIZ(formSchemaId, vizId))),

  setDefaultVisualization: (formSchemaId, vizId) =>
    apiRequest(() => axiosInstance.post(STANDALONE_VIZ_ENDPOINTS.SET_DEFAULT_VIZ(formSchemaId, vizId))),

  /** Card-only: toggle whether this card renders inline in submission sidebars (per-form admin control). */
  setShowInSubmission: (formSchemaId, vizId, show) =>
    apiRequest(() => axiosInstance.post(STANDALONE_VIZ_ENDPOINTS.SET_SHOW_IN_SUBMISSION(formSchemaId, vizId), { show })),

  // ----- List visualizations (ListView): saved data-source / filter / limit config + render -----

  /**
   * Read a ListView viz's saved config (the editor's config panel). The API returns an empty config when none
   * is saved yet.
   * @returns {Promise<{ filters, filterLogic, fromDate, toDate, status, includeInactive, limit, selectedFieldKeys }>}
   */
  getListConfig: (formSchemaId, vizId) =>
    apiRequest(() => axiosInstance.get(STANDALONE_VIZ_ENDPOINTS.LIST_CONFIG_V(formSchemaId, vizId))),

  /** Persist a ListView viz's saved config (data source / filter / limit). */
  saveListConfig: (formSchemaId, vizId, config) =>
    apiRequest(() => axiosInstance.put(STANDALONE_VIZ_ENDPOINTS.LIST_CONFIG_V(formSchemaId, vizId), config || {})),

  /**
   * Fetch the self-contained HTML doc for a ListView viz (plural payload inlined). With no override → renders
   * the viz's SAVED config (GET). With a `filterOverride` (the submissions-list launch path) → POSTs the
   * override config so the rich filter rides in the body, not a query string. Returns the raw HTML string.
   * @param {number} formSchemaId @param {number} vizId
   * @param {object} [filterOverride] same shape as the saved config (filters, filterLogic, …, limit)
   * @returns {Promise<string>} the HTML document
   */
  getRenderListDoc: (formSchemaId, vizId, filterOverride) =>
    apiRequest(() =>
      filterOverride
        ? axiosInstance.post(STANDALONE_VIZ_ENDPOINTS.RENDER_LIST_DOC(formSchemaId, vizId), filterOverride, { responseType: 'text' })
        : axiosInstance.get(STANDALONE_VIZ_ENDPOINTS.RENDER_LIST_DOC(formSchemaId, vizId), { responseType: 'text' })
    ),

  // ----- Per-visualization AI design (vizId-scoped mini-project: view.js + components/*.js) -----

  /** @returns {Promise<{ formSchemaId, vizId, hasDesign, files:Array<{path,content}>, viewJs, lastModel, updatedAt, promptHistory }>} */
  getDesignV: (formSchemaId, vizId) =>
    apiRequest(() => axiosInstance.get(STANDALONE_VIZ_ENDPOINTS.GET_DESIGN_V(formSchemaId, vizId))),

  /** Hand-edit save of ONE mini-project file (view.js or components/{key}.js); server lints. */
  saveDesignFileV: (formSchemaId, vizId, path, content) =>
    apiRequest(() => axiosInstance.put(STANDALONE_VIZ_ENDPOINTS.SAVE_DESIGN_FILE_V(formSchemaId, vizId), { path, content })),

  /** Delete this visualization's design → kit reverts to its generic auto-view. */
  deleteDesignV: (formSchemaId, vizId) =>
    apiRequest(() => axiosInstance.delete(STANDALONE_VIZ_ENDPOINTS.DELETE_DESIGN_V(formSchemaId, vizId))),

  // ----- Version history (snapshots of the authored tree, each tagged with its prompt) -----

  /** @returns {Promise<Array<{ versionNumber, prompt, model, createdAt, files:string[] }>>} newest first. */
  listVersions: (formSchemaId, vizId) =>
    apiRequest(() => axiosInstance.get(STANDALONE_VIZ_ENDPOINTS.LIST_VIZ_VERSIONS(formSchemaId, vizId))),

  /** Restore one version's file tree over the live design (undo a bad authoring pass). */
  restoreVersion: (formSchemaId, vizId, versionNumber) =>
    apiRequest(() => axiosInstance.post(STANDALONE_VIZ_ENDPOINTS.RESTORE_VIZ_VERSION(formSchemaId, vizId, versionNumber))),

  /**
   * Stream AI generation of a brand-new mini-project for ONE visualization.
   * @param {number} formSchemaId
   * @param {number} vizId
   * @param {{ prompt: string, sampleSubmissionId?: number|null }} requestData
   * @param {{ onBuilding?: (msg)=>void, onComplete?: (data)=>void, onError?: (msg)=>void }} callbacks
   *        onComplete receives { files:[{path,content}], summary, savedAt }
   * @param {AbortSignal} [signal]
   */
  generateDesignStreamV: (formSchemaId, vizId, requestData, callbacks, signal) =>
    consumeSseStream(STANDALONE_VIZ_ENDPOINTS.GENERATE_DESIGN_STREAM_V(formSchemaId, vizId), requestData, callbacks, signal),

  /**
   * Stream AI refinement of ONE visualization's existing mini-project (same envelope as generate).
   * @param {number} formSchemaId
   * @param {number} vizId
   * @param {{ message: string, sampleSubmissionId?: number|null }} requestData
   * @param {object} callbacks - same shape as generateDesignStreamV
   * @param {AbortSignal} [signal]
   */
  refineDesignStreamV: (formSchemaId, vizId, requestData, callbacks, signal) =>
    consumeSseStream(STANDALONE_VIZ_ENDPOINTS.REFINE_DESIGN_STREAM_V(formSchemaId, vizId), requestData, callbacks, signal),

  /**
   * Pre-generation triage for one authoring message: routes it to the quick-edit (refine) vs full-redesign
   * (generate) path and returns up to 3 clarifying questions when the request is ambiguous. The server is
   * fail-soft (AI errors degrade to a heuristic mode with no questions); this wrapper also degrades on any
   * network/HTTP failure by returning null, so the editor flow never blocks on preflight.
   * @param {number} formSchemaId @param {number} vizId @param {string} message
   * @returns {Promise<{ mode: 'refine'|'generate'|null, hasDesign: boolean,
   *   questions: Array<{ question: string, options: string[] }> } | null>}
   */
  preflightDesignV: async (formSchemaId, vizId, message) => {
    try {
      return await apiRequest(() =>
        axiosInstance.post(STANDALONE_VIZ_ENDPOINTS.PREFLIGHT_DESIGN_V(formSchemaId, vizId), { message }));
    } catch {
      return null;
    }
  },

  // ---- Per-viz image assets (private; inlined at render via d.asset('name')) ----

  /** List a viz's uploaded images: [{ name, contentType, sizeBytes, uploadedAt, url, reference, description }]. */
  listVizImages: (formSchemaId, vizId) =>
    apiRequest(() => axiosInstance.get(STANDALONE_VIZ_ENDPOINTS.LIST_VIZ_IMAGES(formSchemaId, vizId))),

  /**
   * Upload an image and register it as a per-viz asset. Uses the shared temp-upload presigned-PUT flow, then
   * registers the moved object with the visualization. Returns the created image DTO.
   * @param {number} formSchemaId @param {number} vizId @param {File} file @param {(l,t,p)=>void} [onProgress]
   */
  uploadVizImage: async (formSchemaId, vizId, file, onProgress) => {
    const { blobPath, contentType } = await uploadFileToBlob(file, onProgress);
    return apiRequest(() =>
      axiosInstance.post(STANDALONE_VIZ_ENDPOINTS.REGISTER_VIZ_IMAGE(formSchemaId, vizId), {
        tempBlobPath: blobPath,
        fileName: file.name,
        contentType: contentType || file.type || null,
        sizeBytes: file.size,
      }));
  },

  /** Delete one uploaded image by name. */
  deleteVizImage: (formSchemaId, vizId, name) =>
    apiRequest(() => axiosInstance.delete(STANDALONE_VIZ_ENDPOINTS.DELETE_VIZ_IMAGE(formSchemaId, vizId, name))),

  /** Hand-edit an uploaded image's AI description (no rename). Returns the updated image DTO. */
  updateVizImage: (formSchemaId, vizId, name, description) =>
    apiRequest(() =>
      axiosInstance.put(STANDALONE_VIZ_ENDPOINTS.UPDATE_VIZ_IMAGE(formSchemaId, vizId), { name, description })),

  /** Re-run the vision model on an image to regenerate its description (keeps the name). Returns the image DTO. */
  analyzeVizImage: (formSchemaId, vizId, name) =>
    apiRequest(() =>
      axiosInstance.post(STANDALONE_VIZ_ENDPOINTS.ANALYZE_VIZ_IMAGE(formSchemaId, vizId), { name })),

  /**
   * Queue a background AI image-generation job for this viz. The AI refines the prompt + picks the model
   * unless `model` is set. Returns the job DTO (Queued) including the chosen prompt/model and rationale.
   * @param {number} formSchemaId @param {number} vizId
   * @param {{intent:string, model?:string|null, size?:string|null, maxSize?:number|null, grid?:string|null}} body
   *   `maxSize` downscales the result (px); `grid` ("2x2"|"3x3"|"4x4") makes an icon sheet cropped into a set.
   */
  generateVizImage: (formSchemaId, vizId, body) =>
    apiRequest(() =>
      axiosInstance.post(STANDALONE_VIZ_ENDPOINTS.GENERATE_VIZ_IMAGE(formSchemaId, vizId), body)),

  /** Poll one image-generation job by id. Returns the job DTO (status + chosen params + image names/error). */
  getVizImageJob: (formSchemaId, vizId, jobId) =>
    apiRequest(() =>
      axiosInstance.get(STANDALONE_VIZ_ENDPOINTS.GENERATE_VIZ_IMAGE_JOB(formSchemaId, vizId, jobId))),

  /** Form-tailored image presets for the generate dialog (label + ready prompt + model/size). */
  getVizImagePresets: (formSchemaId, vizId) =>
    apiRequest(() =>
      axiosInstance.get(STANDALONE_VIZ_ENDPOINTS.GENERATE_VIZ_IMAGE_PRESETS(formSchemaId, vizId))),
};

// Exported so sibling services (e.g. demoPageService) reuse the exact SSE envelope parsing / auth / error handling.
export async function consumeSseStream(endpoint, requestData, callbacks, signal) {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';
  const url = `${baseUrl}${endpoint}`;
  let token = null;
  try { token = localStorage.getItem('localToken'); } catch { /* storage blocked — request proceeds without auth header */ }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(requestData),
    signal,
  });

  if (!response.ok) {
    if (response.status === 429) throw new Error('AI rate limit reached. Try again in a moment.');
    if (response.status === 403) throw new Error('Your plan does not allow AI generation. Please upgrade your license.');
    const text = await response.text().catch(() => '');
    throw new Error(text || `Request failed (HTTP ${response.status})`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const parts = buffer.split('\n\n');
    buffer = parts.pop() ?? '';

    for (const part of parts) {
      const line = part.trim();
      if (!line || line.startsWith(':')) continue; // heartbeat or empty
      if (!line.startsWith('data:')) continue;

      const jsonStr = line.slice(5).trim();
      try {
        const event = JSON.parse(jsonStr);
        const { type, data } = event;
        if (type === 'building' && callbacks.onBuilding) callbacks.onBuilding(data?.message ?? '');
        else if (type === 'delta' && callbacks.onDelta) callbacks.onDelta(data?.text ?? '');
        else if (type === 'complete' && callbacks.onComplete) callbacks.onComplete(data ?? {});
        else if (type === 'error' && callbacks.onError) callbacks.onError(data?.message ?? 'AI service error');
      } catch {
        // ignore per-event parse errors — heartbeats and partial chunks are normal
      }
    }
  }
}

export default standaloneVizService;
